import React, { useState } from 'react';
import { sortProjectRows } from '../../utils/functions/sortProjects';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';
import {
  Divider,
  Grid,
  TextField,
  Typography,
  Button,
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  Card,
  CardContent,
  Avatar,
  useTheme,
  alpha,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import 'dayjs/locale/th';
import {
  CalendarToday as CalendarIcon,
  GroupAdd as GroupAddIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Terrain as FieldIcon,
} from '@mui/icons-material';

dayjs.locale('th');

// ฟอร์มขอค่าออกสนาม — ตัดส่วนเวลา/rate ออกจาก OtRequestForm.js เพราะค่าออกสนามเป็น
// flag ต่อวัน (ได้/ไม่ได้) ไม่ใช่ชั่วโมง — คนที่เลือกได้ต้องมี checkin ของวันที่+โครงการ
// ที่เลือกอยู่แล้วเท่านั้น (มาจาก timestamp.rows เหมือน OtRequestForm)
export default function FieldAllowanceRequestForm({
  Controller,
  control,
  timestamp = [],
  project = [],
  selectedEmployees,
  setSelectedEmployees,
  selectedProject,
  setSelectedProject,
  date,
  setDate,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [openEmployeeModal, setOpenEmployeeModal] = useState(false);

  const employeeOptions = Array.isArray(timestamp?.rows)
    ? timestamp?.rows
        .filter((item) => !!item.employee?.field_allowance?.enabled)
        .map((item) => ({
          _id: item._id,
          employee_id: item.employee?._id,
          name: formatEmployeeThaiName(item.employee),
          department: item.employee?.department?.name || '',
          role: item.employee?.role?.name || '',
          checkIn: item.checkIn,
        }))
        .filter(
          (e, i, arr) => e._id && arr.findIndex((x) => x._id === e._id) === i,
        )
    : [];

  const projectOptions = Array.isArray(project?.rows)
    ? sortProjectRows(project?.rows).map((p) => ({
        _id: p._id,
        name: p.name,
        number: p.project_number,
      }))
    : [];

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: isMobile ? 1 : 0 }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          p: isMobile ? 2 : 3,
          mb: isMobile ? 2 : 3,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            sx={{
              bgcolor: alpha(theme.palette.common.white, 0.2),
              width: isMobile ? 48 : 56,
              height: isMobile ? 48 : 56,
            }}
          >
            <FieldIcon fontSize={isMobile ? 'medium' : 'large'} />
          </Avatar>
          <Box>
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={700}>
              แบบฟอร์มขอค่าออกสนาม
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              เลือกโครงการ วันที่ และพนักงานที่ลงเวลาเข้างานแล้ว
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CalendarIcon color="primary" />
                  <Typography variant="h6" fontWeight={600}>วันที่</Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="date"
                  control={control}
                  defaultValue={dayjs()}
                  render={({ field }) => (
                    <DatePicker
                      label="วันที่ขอค่าออกสนาม"
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e);
                        setDate(e);
                      }}
                      renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                      maxDate={dayjs()}
                      disableFuture
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }} />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <BusinessIcon color="primary" />
                  <Typography variant="h6" fontWeight={600}>โครงการ</Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  options={projectOptions}
                  getOptionLabel={(option) =>
                    (option.number ? `${option.number} - ${option.name}` : option.name)
                  }
                  value={selectedProject}
                  onChange={(_, value) => setSelectedProject(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="เลือกโครงการ"
                      size="small"
                      required
                      placeholder="พิมพ์เพื่อค้นหาโครงการ"
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option?._id === value?._id}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" fontWeight={600}>พนักงาน</Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="รายชื่อพนักงาน"
                  placeholder={!selectedProject ? 'กรุณาเลือกโครงการก่อน' : 'คลิกเพื่อเลือกพนักงาน'}
                  disabled={!selectedProject}
                  value={selectedEmployees.length > 0 ? `เลือกแล้ว ${selectedEmployees.length} คน` : ''}
                  onClick={() => { if (selectedProject) setOpenEmployeeModal(true); }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <IconButton
                        edge="end"
                        onClick={() => { if (selectedProject) setOpenEmployeeModal(true); }}
                        disabled={!selectedProject}
                      >
                        <GroupAddIcon />
                      </IconButton>
                    ),
                  }}
                  sx={{
                    '& .MuiInputBase-root': { cursor: !selectedProject ? 'default' : 'pointer' },
                    '& input': { cursor: !selectedProject ? 'default' : 'pointer' },
                  }}
                />

                <Dialog
                  open={openEmployeeModal}
                  onClose={() => setOpenEmployeeModal(false)}
                  maxWidth="sm"
                  fullWidth
                  fullScreen={isMobile}
                >
                  <DialogTitle sx={{ m: 0, p: 2 }}>
                    เลือกพนักงาน
                    {isMobile ? (
                      <IconButton
                        aria-label="close"
                        onClick={() => setOpenEmployeeModal(false)}
                        sx={{ position: 'absolute', right: 8, top: 8, color: (t) => t.palette.grey[500] }}
                      >
                        <CloseIcon />
                      </IconButton>
                    ) : null}
                  </DialogTitle>
                  <DialogContent dividers>
                    <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}`, mb: 2, bgcolor: alpha(theme.palette.background.default, 0.4) }}>
                      <FormControlLabel
                        label={<Typography variant="body2" fontWeight={500}>เลือกทั้งหมด ({employeeOptions.length})</Typography>}
                        control={
                          <Checkbox
                            checked={employeeOptions.length > 0 && selectedEmployees.length === employeeOptions.length}
                            indeterminate={selectedEmployees.length > 0 && selectedEmployees.length < employeeOptions.length}
                            onChange={(e) => {
                              if (e.target.checked) setSelectedEmployees(employeeOptions);
                              else setSelectedEmployees([]);
                            }}
                          />
                        }
                      />
                    </Box>
                    <Box sx={{ maxHeight: isMobile ? 'none' : 400, overflow: 'auto' }}>
                      {employeeOptions.length > 0 ? (
                        <Grid container spacing={1}>
                          {employeeOptions.map((option) => (
                            <Grid item xs={12} sm={6} key={option._id}>
                              <FormControlLabel
                                sx={{ width: '100%', m: 0 }}
                                control={
                                  <Checkbox
                                    checked={selectedEmployees.some((emp) => emp._id === option._id)}
                                    onChange={(e) => {
                                      if (e.target.checked) setSelectedEmployees([...selectedEmployees, option]);
                                      else setSelectedEmployees(selectedEmployees.filter((emp) => emp._id !== option._id));
                                    }}
                                  />
                                }
                                label={<Typography variant="body2">{option.name}</Typography>}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
                          {Array.isArray(timestamp?.rows) && timestamp.rows.length > 0
                            ? 'ไม่มีพนักงานที่มีสิทธิ์ได้รับค่าออกสนามลงเวลาในวันที่/โครงการนี้'
                            : 'กรุณาเลือกวันที่และโครงการเพื่อแสดงรายชื่อพนักงานที่ลงเวลาแล้ว'}
                        </Typography>
                      )}
                    </Box>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setOpenEmployeeModal(false)} variant="contained">ตกลง</Button>
                  </DialogActions>
                </Dialog>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }} />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <DescriptionIcon color="primary" />
                  <Typography variant="h6" fontWeight={600}>เหตุผลในการขอค่าออกสนาม</Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      rows={4}
                      required
                      label="เหตุผล"
                      helperText="กรุณาระบุเหตุผลอย่างละเอียด เพื่อประกอบการพิจารณา"
                      placeholder="เช่น ออกไปติดตั้งหน้างาน, สำรวจพื้นที่, ควบคุมงานที่ไซต์"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  startIcon={<CheckCircleIcon />}
                  sx={{ py: 1.5, borderRadius: 2, fontWeight: 600, fontSize: '1rem' }}
                >
                  ส่งคำขอค่าออกสนาม
                </Button>
              </Grid>
            </Grid>
          </LocalizationProvider>
        </CardContent>
      </Card>
    </Box>
  );
}
